<template>
  <div class="component-type-picker-wrap">
    <select
      class="visually-hidden"
      aria-label="component type"
      :value="modelValue"
      @change="select($event.target.value)"
    >
      <option value="">Choose a type</option>
      <option v-for="type in catalog" :key="type.id" :value="type.id">{{ type.label }}</option>
    </select>
    <div v-if="selected && !listOpen" class="component-type-chosen">
      <div class="component-type-card is-selected" role="status">
        <span class="component-type-icon" aria-hidden="true">
          <font-awesome-icon :icon="selected.icon" />
        </span>
        <span class="component-type-copy">
          <span class="component-type-name">{{ selected.label }}</span>
          <span class="component-type-description">{{ selected.description }}</span>
        </span>
      </div>
      <button type="button" class="component-type-change" @click="listOpen = true">
        change type
      </button>
    </div>
    <template v-else>
      <input
        v-model="query"
        type="search"
        class="form-control form-control-sm component-type-search"
        placeholder="search types, such as table, map, or login"
        aria-label="search component types"
      />
      <div
        class="component-type-picker"
        :class="{ 'is-invalid': invalid }"
        role="listbox"
        aria-label="component type"
      >
        <p v-if="!groups.length" class="component-type-empty">No types match that search.</p>
        <section v-for="group in groups" :key="group.id" class="component-type-group">
          <button
            type="button"
            class="component-type-group-title"
            :aria-expanded="isGroupOpen(group.id) ? 'true' : 'false'"
            @click="toggleGroup(group.id)"
          >
            <font-awesome-icon :icon="isGroupOpen(group.id) ? 'chevron-down' : 'chevron-right'" />
            <span>{{ group.label }}</span>
            <span class="component-type-group-count">{{ group.types.length }}</span>
          </button>
          <div v-show="isGroupOpen(group.id)" class="component-type-grid">
            <button
              v-for="type in group.types"
              :key="type.id"
              type="button"
              class="component-type-card"
              :class="{ 'is-selected': modelValue === type.id }"
              role="option"
              :aria-selected="modelValue === type.id ? 'true' : 'false'"
              @click="select(type.id)"
            >
              <span class="component-type-icon" aria-hidden="true">
                <font-awesome-icon :icon="type.icon" />
              </span>
              <span class="component-type-copy">
                <span class="component-type-name">{{ type.label }}</span>
                <span class="component-type-description">{{ type.description }}</span>
              </span>
            </button>
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import { componentTypeById, componentTypeCatalog, componentTypeGroups, filterComponentTypes } from '../lib/component-type-catalog.js'

function initialCollapsedGroups () {

  const collapsed = {}

  componentTypeGroups(componentTypeCatalog()).forEach((group, index) => {

    if (index > 0) collapsed[group.id] = true
  })

  return collapsed
}

export default {
  name: 'ComponentTypePicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      query: '',
      listOpen: true,
      collapsedGroups: initialCollapsedGroups()
    }
  },
  computed: {
    catalog () {
      return componentTypeCatalog()
    },
    groups () {
      return componentTypeGroups(filterComponentTypes(this.query))
    },
    selected () {
      return componentTypeById(this.modelValue)
    }
  },
  watch: {
    modelValue (value) {
      if (!value) {
        this.query = ''
        this.listOpen = true
      }
    }
  },
  methods: {
    select (id) {
      this.$emit('update:modelValue', id)
      this.query = ''
      this.listOpen = false
    },
    isGroupOpen (id) {
      if (String(this.query || '').trim()) return true
      return !this.collapsedGroups[id]
    },
    toggleGroup (id) {
      if (String(this.query || '').trim()) return
      this.collapsedGroups = Object.assign({}, this.collapsedGroups, {
        [id]: !this.collapsedGroups[id]
      })
    }
  }
}
</script>

<style>
.component-type-search {
  margin-bottom: 10px;
}

.component-type-chosen {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.component-type-chosen .component-type-card {
  flex: 1;
  cursor: default;
}

.component-type-change {
  flex: 0 0 auto;
  align-self: center;
  margin: 0;
  padding: 6px 10px;
  color: #9ad12b;
  background: transparent;
  border: 1px solid #3a3a38;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
}

.component-type-change:hover,
.component-type-change:focus {
  border-color: #9ad12b;
  outline: none;
}

.component-type-picker {
  max-height: 420px;
  overflow: auto;
  padding-right: 4px;
  border-radius: 6px;
}

.component-type-picker.is-invalid {
  outline: 1px solid #f82471;
  outline-offset: 4px;
}

.component-type-empty {
  margin: 8px 0;
  color: #8a8a88;
  font-size: 0.85rem;
}

.component-type-group + .component-type-group {
  margin-top: 14px;
}

.component-type-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0 0 8px;
  padding: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a88;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
}

.component-type-group-title:hover,
.component-type-group-title:focus {
  color: #e8e8e6;
  outline: none;
}

.component-type-group-count {
  margin-left: auto;
  font-weight: 600;
  letter-spacing: 0;
  color: #6a6a67;
}

.component-type-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.component-type-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px;
  text-align: left;
  color: #e8e8e6;
  background: #1e1e1b;
  border: 1px solid #3a3a38;
  border-radius: 6px;
  cursor: pointer;
}

.component-type-card:hover,
.component-type-card:focus {
  border-color: #9ad12b;
  outline: none;
}

.component-type-card.is-selected {
  border-color: #9ad12b;
  background: rgba(154, 209, 43, 0.1);
}

.component-type-icon {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: #2a2a27;
  color: #9ad12b;
  font-size: 13px;
}

.component-type-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.component-type-name {
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.3;
}

.component-type-description {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.35;
  color: #b0b0ae;
}

@media (max-width: 640px) {
  .component-type-grid {
    grid-template-columns: 1fr;
  }
}
</style>
